import React from "react";
import { REFUND_REASONS, REFUND_STATUS_TONE } from "./adminConstants";
import { formatAmountInput, formatCurrency } from "./adminUtils";

const AdminRefundDrawer = ({
  t,
  refundDrawerOpen,
  closeRefundDrawer,
  selectedBooking,
  selectedCurrency,
  refundForm,
  setRefundForm,
  refundBookingLoading,
  refundBookingError,
  refundableAmount,
  refundAmountValue,
  refundFeeEstimate,
  refundNet,
  refundRequiresNote,
  handleRefundSubmit,
  isRefunding,
  refundHistory,
}) => (
  <>
    <div
      className={`admin-drawer__scrim ${
        refundDrawerOpen ? "admin-drawer__scrim--visible" : ""
      }`.trim()}
      role="presentation"
      onClick={closeRefundDrawer}
    />
    <aside
      className={`admin-drawer ${refundDrawerOpen ? "admin-drawer--open" : ""}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="refundDrawerTitle"
    >
      <header className="admin-drawer__header">
        <div>
          <p className="admin-drawer__eyebrow">
            {t("admin.refund.eyebrow", "Owner refunds")}
          </p>
          <h2 id="refundDrawerTitle" className="admin-drawer__title">
            {selectedBooking
              ? t("admin.refund.title", `Refund ${selectedBooking.id}`)
              : t("admin.refund.titleFallback", "Refund booking")}
          </h2>
          {selectedBooking && (
            <p className="admin-drawer__subtitle">
              {selectedBooking.guest} | {selectedBooking.item} |{" "}
              {formatCurrency(selectedBooking.totalPaid, selectedCurrency)}
            </p>
          )}
        </div>
        <button
          type="button"
          className="admin-button admin-button--ghost"
          onClick={closeRefundDrawer}
        >
          {t("admin.actions.close", "Close")}
        </button>
      </header>
      <div className="admin-drawer__body">
        {!selectedBooking ? (
          <p className="admin-empty">
            {t("admin.refund.empty", "Select a booking to issue a refund.")}
          </p>
        ) : (
          <form className="admin-refund" onSubmit={handleRefundSubmit}>
            {refundBookingLoading && (
              <p className="admin-refund__hint">
                {t("admin.refund.loading", "Syncing refund details...")}
              </p>
            )}
            {refundBookingError && (
              <p className="admin-refund__error">{refundBookingError}</p>
            )}
            {refundableAmount <= 0 && (
              <p className="admin-refund__error">
                {t("admin.refund.none", "No refundable balance remaining.")}
              </p>
            )}
            <div className="admin-refund__section">
              <label className="admin-form__label" htmlFor="refundAmount">
                {t("admin.refund.amountLabel", "Refund amount")}
              </label>
              <div className="admin-refund__amount">
                <label className="admin-refund__toggle" htmlFor="refundFullToggle">
                  <input
                    id="refundFullToggle"
                    type="checkbox"
                    checked={refundForm.isFull}
                    onChange={(event) => {
                      const isFull = event.target.checked;
                      setRefundForm((prev) => ({
                        ...prev,
                        isFull,
                        isHalf: false,
                        amount: isFull
                          ? formatAmountInput(refundableAmount)
                          : prev.amount,
                      }));
                    }}
                  />
                  {t("admin.refund.fullToggle", "Full")}
                </label>
                <label className="admin-refund__toggle" htmlFor="refundHalfToggle">
                  <input
                    id="refundHalfToggle"
                    type="checkbox"
                    checked={refundForm.isHalf}
                    onChange={(event) => {
                      const isHalf = event.target.checked;
                      setRefundForm((prev) => ({
                        ...prev,
                        isHalf,
                        isFull: false,
                        amount: isHalf
                          ? formatAmountInput(refundableAmount * 0.5)
                          : prev.amount,
                      }));
                    }}
                  />
                  {t("admin.refund.halfToggle", "50%")}
                </label>
                <input
                  id="refundAmount"
                  className="admin-input admin-refund__amount-input"
                  value={
                    refundForm.isFull
                      ? formatAmountInput(refundableAmount)
                      : refundForm.isHalf
                        ? formatAmountInput(refundableAmount * 0.5)
                      : refundForm.amount
                  }
                  onChange={(event) =>
                    setRefundForm((prev) => ({
                      ...prev,
                      amount: event.target.value,
                    }))
                  }
                  inputMode="decimal"
                  disabled={refundForm.isFull || refundForm.isHalf}
                  aria-label={t("admin.refund.amountField", "Refund amount")}
                />
              </div>
              <p className="admin-refund__hint">
                {t("admin.refund.max", "Max")}{" "}
                {formatCurrency(refundableAmount, selectedCurrency)}
              </p>
              {refundAmountValue > refundableAmount + 0.01 && (
                <p className="admin-refund__error">
                  {t("admin.refund.overLimit", "Refund exceeds remaining paid amount.")}
                </p>
              )}
            </div>
            <div className="admin-refund__section">
              <label className="admin-form__label" htmlFor="refundReason">
                {t("admin.refund.reasonLabel", "Reason")}
              </label>
              <select
                id="refundReason"
                className="admin-select"
                value={refundForm.reason}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, reason: event.target.value }))
                }
              >
                {REFUND_REASONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-refund__summary">
              <div>
                <span>{t("admin.refund.summaryAmount", "Refund amount")}</span>
                <strong>{formatCurrency(refundAmountValue, selectedCurrency)}</strong>
              </div>
              <div>
                <span>{t("admin.refund.summaryFees", "Processor fees")}</span>
                <strong>{formatCurrency(refundFeeEstimate, selectedCurrency)}</strong>
              </div>
              <div className="admin-refund__summary-total">
                <span>{t("admin.refund.summaryNet", "Net refund")}</span>
                <strong>{formatCurrency(refundNet, selectedCurrency)}</strong>
              </div>
            </div>
            <div className="admin-refund__section">
              <label className="admin-form__label" htmlFor="refundNote">
                {t("admin.refund.noteLabel", "Notes")}
              </label>
              <textarea
                id="refundNote"
                className="admin-input admin-input--area"
                value={refundForm.note}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, note: event.target.value }))
                }
                placeholder={t(
                  "admin.refund.notePlaceholder",
                  "Add a note for the audit log"
                )}
                rows={3}
              />
              {refundRequiresNote && !refundForm.note.trim() && (
                <p className="admin-refund__error">
                  {t(
                    "admin.refund.noteRequired",
                    "Notes required for goodwill or other refunds."
                  )}
                </p>
              )}
            </div>
            <div className="admin-refund__section">
              <p className="admin-form__label">{t("admin.refund.history", "Recent refunds")}</p>
              <div className="admin-refund__history">
                {refundHistory.length === 0 ? (
                  <p className="admin-refund__empty">
                    {t("admin.refund.historyEmpty", "No refunds yet.")}
                  </p>
                ) : (
                  <ul className="admin-refund__history-list">
                    {refundHistory.map((refund) => (
                      <li key={refund.id} className="admin-refund__history-item">
                        <span>{refund.time}</span>
                        <span>{formatCurrency(refund.amount, selectedCurrency)}</span>
                        <span
                          className={`admin-pill admin-pill--${
                            REFUND_STATUS_TONE[refund.status] || "neutral"
                          }`}
                        >
                          {refund.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="admin-refund__actions">
              <button
                type="submit"
                className="admin-button admin-button--danger"
                disabled={
                  isRefunding ||
                  refundableAmount <= 0 ||
                  refundAmountValue <= 0 ||
                  refundAmountValue > refundableAmount + 0.01 ||
                  (refundRequiresNote && !refundForm.note.trim())
                }
              >
                {t("admin.refund.issue", "Issue refund")}
              </button>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={closeRefundDrawer}
              >
                {t("admin.actions.cancel", "Cancel")}
              </button>
            </div>
          </form>
        )}
      </div>
    </aside>
  </>
);

export default AdminRefundDrawer;
